// A track rendered whole: the lane as it plays on the timeline, from bar 1
// to the arrangement's end, silence included — so every stem dropped at the
// start of another DAW's timeline lines up with the rest.
//
// Stems are post-fader (level, pan, the lane's devices), dry of sends and
// pre-master, as Live's are: laid side by side at unity they are the mix
// less its returns and master chain.

import { encodeWav24, type PcmSource } from "@web-shared/lib/wav";
import { safeFilename, uniquePaths, type DownloadFile } from "@/lib/download";

/** `source`, as long as `frames` (zeros past its end). Never shortens. */
export function padded(source: PcmSource, frames: number): PcmSource {
  if (frames <= source.length) return source;
  return {
    sampleRate: source.sampleRate,
    numberOfChannels: source.numberOfChannels,
    length: frames,
    getChannelData: (channel) => {
      const out = new Float32Array(frames);
      out.set(source.getChannelData(channel));
      return out;
    },
  };
}

export interface StemLane {
  id: string;
  name: string;
}

/**
 * Render each lane into `<folder>/<track>.wav`, every file `totalSec` long.
 * One at a time, encoded as it goes: a decoded stem is several times the
 * size of its WAV. A lane that fails to render is left out.
 */
export async function stemFiles(
  lanes: StemLane[],
  render: (laneId: string) => Promise<AudioBuffer>,
  totalSec: number,
  folder: string,
  onStep?: (done: number, total: number) => void,
): Promise<DownloadFile[]> {
  const named = uniquePaths(lanes.map((lane) => ({ path: `${folder}/${safeFilename(lane.name)}.wav`, id: lane.id })));
  const files: DownloadFile[] = [];
  onStep?.(0, named.length);
  for (const [i, lane] of named.entries()) {
    try {
      const buffer = await render(lane.id);
      const frames = Math.max(buffer.length, Math.ceil(totalSec * buffer.sampleRate));
      files.push({ path: lane.path, bytes: new Uint8Array(encodeWav24(padded(buffer, frames))) });
    } catch (err) {
      console.warn("[download] stem skipped", lane.path, err);
    }
    onStep?.(i + 1, named.length);
  }
  return files;
}
